import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import useDebounce from "../external/ensolvers-core-frontend-mui/hooks/useDebounce";
import AccessRole from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/core/AccessRole";
import ApiError from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/core/ApiError";
import NotificationService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService";
import { EnsolversOpsUser } from "../models/EnsolversOpsUser";
import { QueryKeyFactory } from "../services/queryKeys";
import UserService from "../services/UserService";
import { AccessRoleType } from "../types/AccessRoleType";
import { Routes } from "../types/Routes";
import { unwrap } from "../utils/queryFn";
import { createTableQuery, refreshTableQuery } from "../utils/tableQuery";

export const userListKeys = new QueryKeyFactory(UserService.path);
const DUPLICATE_MAIL_MESSAGE = "Duplicated mail.";
const CREATE_USER_FAILED_MESSAGE = "Error trying to create User.";

function toUserCreateError(error: unknown): never {
  if (error instanceof ApiError && error.message.includes("core_user_email_UK")) {
    throw new ApiError(error.errorCode, DUPLICATE_MAIL_MESSAGE, error.apiResponse);
  }
  if (error instanceof ApiError) {
    throw new ApiError(error.errorCode, CREATE_USER_FAILED_MESSAGE, error.apiResponse);
  }
  throw error;
}

const useUsersList = (staffRole: string, userStatus: string) => {
  const history = useHistory();
  const params = useLocation().search;
  const queryClient = useQueryClient();

  const [forceUpdateOn, setForceUpdateOn] = useState<number>();
  const [selectedUser, setSelectedUser] = useState<EnsolversOpsUser | undefined>(undefined);
  const [search, setSearch] = useState<string>("");
  const debounceSearchTerm = useDebounce(search, 500);
  const [roles, setRoles] = useState({
    roleAdmin: true,
    roleContractor: false,
    roleManager: false,
    roleHR: false,
    roleHiringManager: false,
  });

  const onDataFetch = useMemo(
    () =>
      createTableQuery<EnsolversOpsUser>({
        queryKey: (page, pageSize, sortBy, sortDir) =>
          userListKeys.list({ page, pageSize, sortBy, sortDir, search, staffRole, userStatus }),
        queryFn: (page, pageSize) => unwrap(UserService.findByRoleAndUserName(page, pageSize, search, staffRole, userStatus)),
        client: queryClient,
      }),
    [queryClient, search, staffRole, userStatus],
  );

  const refreshTable = () => refreshTableQuery(userListKeys.lists(), () => setForceUpdateOn(Date.now()), queryClient);

  useEffect(() => {
    const username = new URLSearchParams(params).get("username");
    if (username !== null) {
      setSearch(username);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => updateCurrentHistoryEntry(), []); // eslint-disable-line react-hooks/exhaustive-deps

  const statusMutation = useMutation({
    mutationFn: ({ externalId, enable }: { externalId: string; enable: boolean }) =>
      unwrap(UserService.changeUserStatus(externalId, enable)),
    onSuccess: async () => {
      setSelectedUser(undefined);
      await refreshTable();
      NotificationService.info("User updated!");
    },
  });

  const createMutation = useMutation({
    mutationFn: async (user: EnsolversOpsUser) => {
      try {
        return await unwrap(UserService.create(user));
      } catch (error) {
        toUserCreateError(error);
      }
    },
    onSuccess: async () => {
      await refreshTable();
      NotificationService.info("User created!");
    },
  });

  const updateMutation = useMutation({
    mutationFn: (user: EnsolversOpsUser) => unwrap(UserService.update(user)),
    onSuccess: async () => {
      await refreshTable();
      NotificationService.info("User updated!");
    },
  });

  const validUser = (user: EnsolversOpsUser): string => {
    if (new Date(user.vacationsComputedSince!) < new Date(user.shortStart)) {
      return "Vacations Computed Since must be equal or greater than Start Date";
    }
    if (user.vacationAccumulation! < 0 || user.vacationAccumulation! > 30) {
      return "Vacations accumulation per year should be positive and less or equal than 30 days";
    }
    return "OK";
  };

  const handleUserStatusChange = () => {
    if (!selectedUser?.externalId) return;
    statusMutation.mutate({ externalId: selectedUser.externalId, enable: !selectedUser.enabled });
  };

  const handleUserCreation = (user: EnsolversOpsUser) => {
    const userValidationMessage = validUser(user);
    if (userValidationMessage !== "OK") {
      NotificationService.error(userValidationMessage);
      return;
    }
    return createMutation.mutateAsync(user).then(() => undefined);
  };

  const handleUserUpdate = (user: EnsolversOpsUser) => {
    if (new Date(user.vacationsComputedSince!) < new Date(user.shortStart)) {
      NotificationService.error("Error trying to update user. Vacations Computed Since must be equal or greater than Start Date");
      return;
    }
    return updateMutation.mutateAsync(user).then(() => undefined);
  };

  const setRolesForUser = async (user: EnsolversOpsUser) => {
    setRoles({
      roleAdmin: user.roles.some((role) => role.name.toString() === AccessRoleType.ROLE_ADMIN),
      roleContractor: user.roles.some((role) => role.name.toString() === AccessRoleType.ROLE_CONTRACTOR),
      roleManager: user.roles.some((role) => role.name.toString() === AccessRoleType.ROLE_MANAGER),
      roleHR: user.roles.some((role) => role.name.toString() === AccessRoleType.ROLE_HR),
      roleHiringManager: user.roles.some((role) => role.name.toString() === AccessRoleType.ROLE_HIRING_MANAGER),
    });
  };

  const roleMutation = useMutation({
    mutationFn: ({ email, role, state }: { email: string; role: string; state: boolean }) =>
      unwrap(UserService.setRole(email, role, state)),
    onSuccess: async (user) => {
      await setRolesForUser(user);
      setSelectedUser(user);
      await refreshTable();
      NotificationService.info("The data has been updated successfully!");
    },
  });

  const handleRoleChange = (role: string, checked: boolean) => {
    if (!selectedUser?.email) return;
    roleMutation.mutate({ email: selectedUser.email, role, state: checked });
  };

  const goPersonalInfoPage = (user: EnsolversOpsUser) => {
    if (!user) return;
    history.push(`${Routes.PROFILE}/${user.externalId}`);
  };

  const updateCurrentHistoryEntry = () => {
    if (search === "") {
      history.replace(`${Routes.USERS}`);
    } else {
      history.replace(`${Routes.USERS}?username=${search}`);
    }
  };

  const getRoles = (roles: AccessRole[]) => {
    let roleString: string = "";
    roles.every((r) => (roleString = roleString.concat(" ", r.name.replace("ROLE_", ""), ",")));
    return roleString.replace(/,$/, "");
  };

  return {
    search,
    setSearch,
    debounceSearchTerm,
    forceUpdateOn,
    onDataFetch,
    getRoles,
    setSelectedUser,
    goPersonalInfoPage,
    setRolesForUser,
    handleRoleChange,
    selectedUser,
    handleUserStatusChange,
    handleUserCreation,
    handleUserUpdate,
    roles,
  };
};

export default useUsersList;
